import { lazy, StrictMode, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Self-hosted variable fonts. No request leaves the origin on first paint —
// a product that tells people the server cannot read their messages should not
// open by telling a font CDN who just arrived.
import '@fontsource-variable/archivo';
import '@fontsource-variable/geist-mono';
import './styles/global.css';
import './styles/modern.css';

import { installAppFeel } from './app/appFeel.js';
import { useApplyTheme } from './app/theme.js';
import { useApplyUiScale } from './app/uiScale.js';
import { TitleBar, isDesktopApp } from './app/TitleBar.js';
import { AuthScreen } from './auth/AuthScreen.js';
import { CoreconfMark } from './brand/CoreconfMark.js';
import { ApiError, users, type SessionUser } from './api/client.js';
import { UpdateGate } from './update/UpdatePrompt.js';
import { LaunchGate } from './update/LaunchGate.js';
import { useUpdateChecker } from './update/useUpdate.js';

/*
 * The authenticated product includes MLS/CoreCrypto, voice, administration
 * and every conversation surface. None of it can render before `/users/@me`
 * says there is a session, so shipping it in the signed-out page's first
 * chunk only made login pay to parse code it could not use. The split is on
 * the real authorization boundary; after sign-in the shell loads once and is
 * then cached like any other application chunk.
 */
const AppShell = lazy(() =>
  import('./app/AppShell.js').then((module) => ({ default: module.AppShell })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Authorization failures are not transient. Retrying a 401 four times
      // just delays the sign-in screen the user needs to see.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 15_000,
    },
  },
});

type AuthState =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; user: SessionUser };

function Root(): React.ReactElement {
  useUpdateChecker();
  // Applied at the root so the sign-in screen honours it too.
  useApplyTheme();
  useApplyUiScale();

  /*
   * The desktop window draws its own chrome, so the document has to make room
   * for it. Done once on the root element rather than in every screen, because
   * the sign-in screen needs the bar too — a frameless window with no way to
   * close it before you log in is a window you have to kill from the taskbar.
   */
  useEffect(() => {
    if (isDesktopApp) document.documentElement.dataset['desktop'] = 'true';
  }, []);
  const [state, setState] = useState<AuthState>({ status: 'checking' });

  /*
   * Session restoration.
   *
   * The session lives in an httpOnly cookie, which this code cannot read — so
   * "am I signed in" is a question only the server can answer. Asking it once
   * on boot is the price of not exposing the token to JavaScript, and it is
   * the right trade.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // One request answers both questions: whether the cookie is still valid,
        // and who it belongs to. Asking the session list instead told us we were
        // signed in without telling us as whom.
        const me = await users.me();
        if (!cancelled) {
          setState({ status: 'signed-in', user: { id: me.id, username: me.username } });
        }
      } catch {
        // 401 is the ordinary "not signed in" case, not an error worth showing.
        if (!cancelled) setState({ status: 'signed-out' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <TitleBar title="coreconf" />
      <a className="skip-link" href="#main-content">
        Ana içeriğe geç
      </a>
      {state.status === 'checking' ? (
        <div id="main-content" tabIndex={-1} style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
          <CoreconfMark size={56} />
        </div>
      ) : state.status === 'signed-out' ? (
        <AuthScreen onAuthenticated={(user) => setState({ status: 'signed-in', user })} />
      ) : (
        <Suspense
          fallback={
            <div className="app-loading" role="status">
              <CoreconfMark size={56} />
              <span>Uygulama hazırlanıyor…</span>
            </div>
          }
        >
          <AppShell
            user={state.user}
            onSignedOut={() => {
              queryClient.clear();
              setState({ status: 'signed-out' });
            }}
          />
        </Suspense>
      )}
    </>
  );
}

/*
 * Development harness.
 *
 * Gated on `import.meta.env.DEV`, which Vite replaces with a literal `false` in
 * a production build — so the import is eliminated entirely and the chunk is
 * never emitted. The dynamic import alone was not enough: it kept the code out
 * of the *initial* bundle but still shipped it, and this harness **registers
 * accounts**. Anyone who found the URL on a deployed instance would create one.
 */
if (import.meta.env.DEV && location.search.includes('e2ee-harness')) {
  void import('./e2ee/harness.js').then(async ({ runHarness }) => {
    const role = location.search.includes('responder') ? 'responder' : 'initiator';
    const params = new URLSearchParams(location.search);
    const peer = params.get('peer') ?? undefined;
    const server = params.get('server') ?? undefined;
    const result = await runHarness(role, peer, server);
    (window as unknown as { __harness: unknown }).__harness = result;
    console.log('[e2ee harness]', JSON.stringify(result, null, 2));
  });
}

// Reload keys, the native context menu and drag-select: the seams of the tab
// this runs in. Installed before first paint so no frame of the app behaves
// like a web page.
installAppFeel();

const container = document.getElementById('root');
if (container === null) throw new Error('#root missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* The launch check wraps the app, not the other way round: the point is
          that nothing renders until we know we are running the current build. */}
      <LaunchGate>
        <Root />
      </LaunchGate>
      {/* The blocking update gate sits above everything, including sign-in. */}
      <UpdateGate />
    </QueryClientProvider>
  </StrictMode>,
);
